/**
 * New group (plan docs/plans/lms-plan.md 8.2, 5.3, 18.20; WP-43; R6, R19).
 *
 * A name, a kind and an optional description. The kind is fixed once the
 * group exists (the server refuses a kind change): a requirement profile
 * assigns its required courses to its members on its own (R19); a saved
 * audience is a list of people to send courses to (R6). A duplicate name in
 * the network is 409 LMS_CONFLICT carrying existing_group { id, is_active }
 * (18.20): the dialog says so and offers to open the existing group, or to
 * reactivate it when it was deactivated.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { useToast } from '../../../../ui/Toast';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import {
  createGroup,
  existingGroupOf,
  GROUP_DESCRIPTION_MAX,
  GROUP_NAME_MAX,
  updateGroup,
} from '../../../api/groups';
import type { ExistingGroupRef, GroupKind, LmsGroup } from '../../../api/groups';
import { ErrorNote, labelCls, metaCls } from '../../../components/pickers/shared';

export interface NewGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new (or reactivated, or existing) group's id to open it. */
  onOpenGroup: (groupId: string) => void;
}

const KIND_CHOICES: Array<{ kind: GroupKind; label: string; hint: string }> = [
  {
    kind: 'profile',
    label: 'Requirement profile',
    hint: "Its members are assigned the courses it requires, automatically, with a due window (a role curriculum).",
  },
  {
    kind: 'adhoc',
    label: 'Saved audience',
    hint: "A list of people to send courses and scenarios to. It requires nothing on its own.",
  },
];

export function NewGroupModal({ isOpen, onClose, onOpenGroup }: NewGroupModalProps) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GroupKind>('profile');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<ExistingGroupRef | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setKind('profile');
    setDescription('');
    setSaving(false);
    setError(null);
    setExisting(null);
  }, [isOpen]);

  const trimmed = name.trim();
  const nameError = trimmed.length > GROUP_NAME_MAX ? `A name is at most ${GROUP_NAME_MAX} characters.` : null;
  const descriptionError =
    description.trim().length > GROUP_DESCRIPTION_MAX ? `A description is at most ${GROUP_DESCRIPTION_MAX} characters.` : null;
  const canSave = trimmed.length > 0 && !nameError && !descriptionError && !saving;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setExisting(null);
    try {
      const group: LmsGroup = await createGroup({ name: trimmed, kind, description: description.trim() || null });
      toast(`Created ${group.name}.`, { type: 'success' });
      onOpenGroup(group.id);
    } catch (err) {
      setExisting(existingGroupOf(err));
      setError(apiErrorMessage(err, 'Could not create the group.'));
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async () => {
    if (!existing) return;
    setSaving(true);
    try {
      const { group } = await updateGroup(existing.id, { is_active: true });
      toast(`Reactivated ${group.name}.`, { type: 'success' });
      onOpenGroup(group.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reactivate the group.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New group" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4" data-testid="new-group-form">
        <div>
          <label htmlFor="lms-new-group-name" className={labelCls}>
            Name
          </label>
          <input
            id="lms-new-group-name"
            data-testid="new-group-name"
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
            value={name}
            maxLength={GROUP_NAME_MAX + 20}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          {nameError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{nameError}</p>}
        </div>

        <fieldset>
          <legend className={labelCls}>Kind</legend>
          <div className="space-y-2">
            {KIND_CHOICES.map((c) => (
              <label
                key={c.kind}
                className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors duration-150 sm:min-h-0 ${
                  kind === c.kind
                    ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                    : 'border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50'
                }`}
              >
                <input
                  type="radio"
                  name="lms-new-group-kind"
                  value={c.kind}
                  checked={kind === c.kind}
                  onChange={() => setKind(c.kind)}
                  data-testid={`new-group-kind-${c.kind}`}
                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-2 focus:ring-primary-500"
                />
                <span>
                  <span className="block font-medium text-gray-900 dark:text-gray-100">{c.label}</span>
                  <span className={metaCls}>{c.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <p className={`${metaCls} mt-2`}>The kind cannot be changed later.</p>
        </fieldset>

        <div>
          <label htmlFor="lms-new-group-description" className={labelCls}>
            Description <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
          </label>
          <textarea
            id="lms-new-group-description"
            data-testid="new-group-description"
            className={`${fieldCls} w-full`}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {descriptionError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{descriptionError}</p>}
        </div>

        {error && (
          <ErrorNote testId="new-group-error">
            <p>{error}</p>
            {existing && (
              <div className="mt-2 flex flex-wrap gap-2">
                {existing.is_active ? (
                  <button type="button" className={secondaryBtnCls} onClick={() => onOpenGroup(existing.id)} data-testid="open-existing-group">
                    Open the existing group
                  </button>
                ) : (
                  <button type="button" className={secondaryBtnCls} onClick={reactivate} disabled={saving} data-testid="reactivate-existing-group">
                    Reactivate the existing group
                  </button>
                )}
              </div>
            )}
          </ErrorNote>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={!canSave} data-testid="new-group-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create group
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default NewGroupModal;
